import { useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  LoadMore,
  LoadingBlock,
  PageHeader,
  Select,
  StatusBadge,
  Table,
  Td,
  Th,
  buttonStyles,
} from "../../components/ui";
import { CopyButton } from "../../components/CopyButton";
import { TxLink } from "../../components/TxLink";
import { API_URL, ApiError } from "../../lib/api";
import { daysSince, formatDate, formatWaitingFor, shortAddress } from "../../lib/format";
import { formatCount, formatMoney } from "../../lib/money";
import { PAYOUT_STATES } from "../../lib/states";
import {
  useApprovePayout,
  useMarkPayoutFailed,
  useMarkPayoutPaid,
  usePayoutHistory,
  usePayoutQueue,
  useRejectPayout,
} from "../../lib/queries";
import type { QueuedPayout } from "../../lib/types";
import { useTranslation } from "react-i18next";
import type { ParseKeys } from "i18next";

/**
 * Money leaving the programme: what is waiting on a decision, and what already
 * went.
 *
 * Two tables and they are not the same table filtered. The queue is a
 * *worklist* — short by design, unpaged, and if it is ever long enough to need
 * a second page the programme has a problem a second page would hide. The
 * history is the record: it only grows, it pages, and it is what somebody
 * reconciling the treasury wallet against the ledger reads.
 */
export function AdminPayoutsPage() {
  const { t } = useTranslation("admin");
  return (
    <>
      <PageHeader
        title={t("payouts.title")}
        description={t("payouts.description")}
      />
      <PayoutQueue />
      <PayoutHistory />
    </>
  );
}

/**
 * The review queue.
 *
 * Every row is a decision about money that cannot be recalled once made, so the
 * row carries the context the decision is made on rather than sending somebody
 * to another screen for it: who they are, how long they have been here, how
 * many referrals actually converted, and the address the money would go to.
 */
/**
 * The worklist, and it now has two stops rather than one.
 *
 * A `requested` payout needs a decision: approve or refuse. An `approved` one
 * needs a **transfer** — somebody sends USDC from the treasury and records the
 * hash here. Those are two different jobs and they may well be two different
 * people on two different days, so the row shows the controls for whichever
 * stop it is at and nothing else.
 *
 * Recording is deliberately a second step and not folded into approval. The
 * transfer can fail, be sent for the wrong amount, or simply not happen because
 * whoever approved it went home — and a payout reading "paid" beside money that
 * never moved is the one lie this dashboard must not tell.
 */
function PayoutQueue() {
  const { t } = useTranslation("admin");
  const queue = usePayoutQueue();
  const approve = useApprovePayout();
  const reject = useRejectPayout();
  const markPaid = useMarkPayoutPaid();
  const markFailed = useMarkPayoutFailed();

  /* Which row is open, and for what. One at a time: two half-filled forms in
     one table is a way to paste a hash into the wrong payout. */
  const [open, setOpen] = useState<{ id: string; mode: "reject" | "settle" | "fail" } | null>(null);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const busy =
    approve.isPending || reject.isPending || markPaid.isPending || markFailed.isPending;

  async function act(run: () => Promise<unknown>) {
    setError(null);
    try {
      await run();
      setOpen(null);
      setText("");
    } catch (cause) {
      /*
       * The refusals that matter are "you cannot approve your own payout",
       * "you cannot settle your own payout" and the duplicate-hash conflict —
       * all sentences written for a person. Shown as they are rather than
       * mapped: somebody who hits one needs to know another reviewer has to
       * look, or that the hash is already on another payout.
       */
      setError(cause instanceof ApiError ? cause.message : t("payouts.failed"));
    }
  }

  function start(id: string, mode: "reject" | "settle" | "fail") {
    setOpen({ id, mode });
    setText("");
    setError(null);
  }

  return (
    <Card>
      <CardHeader
        title={t("payouts.queue.title")}
        description={t("payouts.queue.description")}
      />
      {error && (
        <div className="px-4 pt-4">
          <Alert>{error}</Alert>
        </div>
      )}
      {queue.isPending ? (
        <LoadingBlock />
      ) : queue.error ? (
        <div className="px-4 py-4">
          <Alert>{queue.error.message}</Alert>
        </div>
      ) : queue.data.length === 0 ? (
        <EmptyState
          title={t("payouts.queue.empty")}
          description={t("payouts.queue.emptyDescription")}
        />
      ) : (
        <Table
          head={
            <tr>
              <Th>{t("payouts.th.affiliate")}</Th>
              <Th priority="md">{t("payouts.th.sendTo")}</Th>
              {/* How long they have been waiting, and it is the column this
                  queue was missing. Without it a payout requested six days ago
                  looks exactly like one from six minutes ago, and a reviewer
                  scanning ten rows does not notice the person being left. */}
              <Th>{t("payouts.th.waiting")}</Th>
              <Th>{t("payouts.th.status")}</Th>
              <Th className="text-right">{t("payouts.th.amount")}</Th>
              <Th className="text-right" />
            </tr>
          }
        >
          {queue.data.map((payout) => (
            <QueueRow
              key={payout.id}
              payout={payout}
              busy={busy}
              open={open?.id === payout.id ? open.mode : null}
              text={text}
              onText={setText}
              onStart={(mode) => start(payout.id, mode)}
              onClose={() => setOpen(null)}
              onApprove={() => void act(() => approve.mutateAsync(payout.id))}
              onReject={() =>
                void act(() => reject.mutateAsync({ payoutId: payout.id, reason: text.trim() }))
              }
              onSettle={() =>
                void act(() => markPaid.mutateAsync({ payoutId: payout.id, txHash: text.trim() }))
              }
              onFail={() =>
                void act(() => markFailed.mutateAsync({ payoutId: payout.id, reason: text.trim() }))
              }
            />
          ))}
        </Table>
      )}
    </Card>
  );
}

function QueueRow({
  payout,
  busy,
  open,
  text,
  onText,
  onStart,
  onClose,
  onApprove,
  onReject,
  onSettle,
  onFail,
}: {
  payout: QueuedPayout;
  busy: boolean;
  open: "reject" | "settle" | "fail" | null;
  text: string;
  onText: (value: string) => void;
  onStart: (mode: "reject" | "settle" | "fail") => void;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  onSettle: () => void;
  onFail: () => void;
}) {
  const { t } = useTranslation("admin");
  const { t: ts } = useTranslation("states");
  const state = PAYOUT_STATES[payout.status];
  const decidable = payout.status === "requested";
  const settleable = payout.status === "approved";
  /* Two days. Long enough that a weekend does not mark every row, short enough
     that somebody waiting on money is surfaced before they write in. */
  const stale = daysSince(payout.requestedAt) >= 2;

  return (
    <tr>
      <Td>
        <div className="space-y-0.5">
          <Link
            className="text-sm text-fg transition-colors duration-200 ease-smooth hover:text-accent"
            to={`/admin/affiliates/${payout.affiliateId}`}
          >
            {payout.displayName || "—"}
          </Link>
          {/* The review, in one line: how long they have been here and how many
              referrals actually converted. A first payout requested days after
              joining, on eleven conversions, is the shape worth a second look. */}
          <p className="text-[12px] whitespace-nowrap text-fg-subtle">
            {t("payouts.row.summary", { converted: formatCount(payout.conversions), date: formatDate(payout.affiliateSince) })}
            {payout.affiliateStatus !== "active" && ` · ${t(`affiliates.status.${payout.affiliateStatus}`)}`}
          </p>
        </div>
      </Td>
      <Td priority="md">
        {/*
         * The destination, and on an approved row it is what somebody is about
         * to paste into a wallet.
         *
         * At `md` rather than `lg` since it became a copy control: it now costs
         * a fraction of the width it did as forty wrapped characters, and a row
         * that offers **Record transfer** while hiding the address that was
         * supposedly sent to is incoherent.
         *
         * A copy control rather than the full string, which is a correction:
         * the full address was here on the argument that a shortened one is a
         * thing to mistype — true of text somebody retypes, and irrelevant once
         * the exact value is one click away. What it cost was two wrapped lines
         * of monospace in every row of a table read by scanning, and an admin
         * hand-selecting forty hex characters is the risk it was trying to
         * avoid.
         *
         * The shortened form still shows both ends, which is what somebody
         * checks an address against.
         */}
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-[12px] whitespace-nowrap text-fg-muted" title={payout.payoutAddress}>
            {shortAddress(payout.payoutAddress)}
          </span>
          <CopyButton value={payout.payoutAddress} label="" />
        </div>
      </Td>
      <Td className="whitespace-nowrap">
        {/*
         * Marked past two days rather than coloured on a gradient. The decision
         * is binary — look at this now, or carry on down the list — and a scale
         * of ambers would make every row a judgement instead of one.
         */}
        {stale ? (
          <span className="inline-flex items-center gap-1.5 text-[13px] text-warn">
            <span className="size-1.5 rounded-full bg-warn" aria-hidden="true" />
            {formatWaitingFor(payout.requestedAt)}
          </span>
        ) : (
          <span className="text-[13px] text-fg-muted">{formatWaitingFor(payout.requestedAt)}</span>
        )}
      </Td>
      <Td>
        <div className="min-w-40 space-y-0.5">
          <StatusBadge tone={state.tone} label={ts(state.label)} />
          {payout.failureReason && (
            <p className="text-[12px] text-fg-subtle">{payout.failureReason}</p>
          )}
          {settleable && !payout.failureReason && (
            <p className="text-[12px] text-fg-subtle">{t("payouts.row.sendThenRecord")}</p>
          )}
        </div>
      </Td>
      <Td className="text-right tabular-nums">
        {formatMoney(payout.amountMicros, payout.currency)}
      </Td>
      <Td className="text-right">
        {open === "reject" || open === "fail" ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Input
              value={text}
              onChange={(event) => onText(event.target.value)}
              placeholder={t("payouts.row.reasonPlaceholder")}
              className="h-8 w-56"
              maxLength={500}
              autoFocus
            />
            <Button
              variant="danger"
              size="sm"
              loading={busy}
              disabled={!text.trim()}
              onClick={open === "reject" ? onReject : onFail}
            >
              {open === "reject" ? t("payouts.row.refuse") : t("payouts.row.markFailed")}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t("payouts.row.cancel")}
            </Button>
          </div>
        ) : open === "settle" ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Input
              value={text}
              onChange={(event) => onText(event.target.value)}
              placeholder={t("payouts.row.hashPlaceholder")}
              className="h-8 w-72 font-mono"
              maxLength={80}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
            />
            <Button variant="primary" size="sm" loading={busy} disabled={!text.trim()} onClick={onSettle}>
              {t("payouts.row.record")}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t("payouts.row.cancel")}
            </Button>
          </div>
        ) : decidable ? (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => onStart("reject")}>
              {t("payouts.row.refuse")}
            </Button>
            <Button variant="primary" size="sm" loading={busy} onClick={onApprove}>
              {t("payouts.row.approve")}
            </Button>
          </div>
        ) : settleable ? (
          <div className="flex items-center justify-end gap-1">
            {/* The escape hatch, and it is not a failure state to hide: a
                transfer that reverted or an address that turns out to be a
                contract leaves an approved payout nobody can settle, and
                without this it sits in the queue for ever holding its
                commissions hostage. */}
            <Button variant="ghost" size="sm" onClick={() => onStart("fail")}>
              {t("payouts.row.didNotSend")}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => onStart("settle")}>
              {t("payouts.row.recordTransfer")}
            </Button>
          </div>
        ) : null}
      </Td>
    </tr>
  );
}

const FILTERS: { value: string; label: ParseKeys<"admin"> }[] = [
  { value: "", label: "payouts.filters.all" },
  { value: "requested", label: "payouts.filters.requested" },
  { value: "approved", label: "payouts.filters.approved" },
  { value: "processing", label: "payouts.filters.processing" },
  { value: "paid", label: "payouts.filters.paid" },
  { value: "failed", label: "payouts.filters.failed" },
  { value: "rejected", label: "payouts.filters.rejected" },
  { value: "cancelled", label: "payouts.filters.cancelled" },
];

/**
 * Every payout ever, and the export beside it.
 *
 * The export is a commission-level CSV rather than a payout-level one, which is
 * the asymmetry worth explaining: a payout is one transfer and reconciles
 * against the treasury wallet by hash, while the question that actually needs a
 * spreadsheet is *what made up that amount* — and that is commissions.
 *
 * It is a plain link and not a fetch. The response carries
 * `Content-Disposition: attachment`, so the browser saves it and never
 * navigates; downloading it through JavaScript would mean holding the whole
 * file in memory to build a blob URL, for a file the browser will stream on its
 * own. `target="_blank"` so a refusal lands somewhere other than on top of this
 * screen.
 */
function PayoutHistory() {
  const { t } = useTranslation("admin");
  const { t: ts } = useTranslation("states");
  const [status, setStatus] = useState("");
  const history = usePayoutHistory(status);
  const rows = history.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <Card>
      <CardHeader
        title={t("payouts.history.title")}
        description={t("payouts.history.description")}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="h-8 w-44"
              aria-label={t("payouts.history.filter")}
            >
              {FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {t(filter.label)}
                </option>
              ))}
            </Select>
            <a
              className={buttonStyles("secondary", "sm")}
              href={`${API_URL}/v1/affiliate/admin/commissions.csv`}
              target="_blank"
              rel="noreferrer"
            >
              {t("payouts.history.export")}
            </a>
          </div>
        }
      />
      {history.isPending ? (
        <LoadingBlock />
      ) : history.error ? (
        <div className="px-4 py-4">
          <Alert>{history.error.message}</Alert>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={status ? t("payouts.history.noneWithStatus") : t("payouts.history.empty")}
          description={
            status ? t("payouts.history.clearFilter") : t("payouts.history.emptyDescription")
          }
        />
      ) : (
        <Table
          head={
            <tr>
              <Th>{t("payouts.th.affiliate")}</Th>
              <Th>{t("payouts.th.requested")}</Th>
              <Th priority="md">{t("payouts.th.completed")}</Th>
              <Th>{t("payouts.th.status")}</Th>
              <Th className="text-right">{t("payouts.th.amount")}</Th>
            </tr>
          }
        >
          {rows.map((payout) => {
            const state = PAYOUT_STATES[payout.status];
            return (
              <tr key={payout.id}>
                <Td>
                  {/* Linked, because the row after a refused payout is almost
                      always "why", and the answer is on their detail screen. */}
                  <Link
                    className="text-sm text-fg transition-colors duration-200 ease-smooth hover:text-accent"
                    to={`/admin/affiliates/${payout.affiliateId}`}
                  >
                    {payout.displayName || "—"}
                  </Link>
                </Td>
                <Td className="whitespace-nowrap text-fg-muted">
                  {formatDate(payout.requestedAt)}
                </Td>
                <Td priority="md" className="whitespace-nowrap text-fg-muted">
                  {payout.completedAt ? formatDate(payout.completedAt) : "—"}
                </Td>
                <Td>
                  {/*
                   * A failed row says why; a paid row shows the transfer. The
                   * symmetry is the point — both answer "what happened to this
                   * money", and the hash is the half somebody reconciling the
                   * treasury wallet is actually looking for.
                   */}
                  <div className="min-w-56 space-y-0.5">
                    <StatusBadge tone={state.tone} label={ts(state.label)} />
                    {payout.failureReason && (
                      <p className="text-[12px] text-fg-subtle">{payout.failureReason}</p>
                    )}
                    {payout.txHash && <TxLink payout={payout} />}
                  </div>
                </Td>
                <Td className="text-right tabular-nums">
                  {formatMoney(payout.amountMicros, payout.currency)}
                </Td>
              </tr>
            );
          })}
        </Table>
      )}
      <LoadMore
        hasMore={history.hasNextPage}
        loading={history.isFetchingNextPage}
        onMore={() => void history.fetchNextPage()}
        loaded={rows.length}
        noun="payouts"
      />
    </Card>
  );
}
